import {useContext, useRef, useState} from "react";
import {ToastContext} from "Toast";
import {useMessages} from "i18n";
import {useLoginModal} from "login/LoginModalProvider";
import type {CouponClaimActions} from "offers/ladder/CouponClaimDialog";
import {paymentAdmission} from "pay/withdrawalRight";
import {useEmbeddedSubscriptionCheckout} from "pay/useEmbeddedSubscriptionCheckout";
import type {CheckoutDocumentRead} from "pay/checkoutDocument";
import {useUser} from "UserProvider";
import {couponGiftPrice} from "offers/ladder/couponEvents";
import {usePaidMoment} from "upsell/usePaidMoment";
import type {PromoGift} from "./PromoOfferApi";
import {resumePromoCheckout} from "./promoLink";

/**
 * The promo-link claim, satisfying {@link CouponClaimActions} so the coupon dialog needs no promo
 * branch (kitecorp/transferify-ui#2343) — the same one-view-two-adapters split the referral picker
 * uses.
 *
 * The continuation rule lives in `./promoLink`'s `resumePromoCheckout`; this only supplies its
 * ports. `user` reads a render-updated ref rather than the closure: `useLoginModal().open` resolves
 * only after the app session commits its authenticated state — the account publish lands in the same
 * sequence — so the ref is fresh by the time the continuation re-judges eligibility, where the
 * closure would still hold the signed-out visitor.
 *
 * `expired` is the mint's refusal (`error.pay.offerLadderTokenInvalid` → `offerRefused`): a code
 * sold out or disabled while the dialog was open reads as the offer being GONE, stated in the
 * dialog's own words, never as a checkout error and never as a full-price retry.
 *
 * <b>Its funnel is `PROMO_LINK`</b> (kitecorp/transferify-ui#2914): the dialog on screen with its
 * figure, the Subscribe press, the checkout and the payment. The dialog opens for a signed-out visitor
 * too, so its showing and press count them (owner 2026-09-27); the checkout follows the sign-in the
 * press raises. Before this, a purchase through a promo link recorded no surface at all.
 */
export function usePromoClaim(
	offer: PromoGift, open: boolean, activeVisit: () => boolean, onClose: () => void,
): CouponClaimActions & {dismiss: () => void} {
	const {code} = offer;
	const price = couponGiftPrice(offer.gift);
	const funnel = usePaidMoment("PROMO_LINK", {visible: open, price});
	const inPage = useEmbeddedSubscriptionCheckout();
	const [acknowledged, setAcknowledged] = useState(false);
	const [user] = useUser();
	const userRef = useRef(user);
	userRef.current = user;
	const {open: openLogin} = useLoginModal();
	const {banner} = useMessages();
	const {showSuccessToast} = useContext(ToastContext);
	const subscribe = (readSelection?: () => CheckoutDocumentRead | undefined) => {
		if (inPage.busy || inPage.offerRefused || paymentAdmission(acknowledged) === "acknowledgementRequired") {
			return;
		}
		funnel.reportClicked();
		void resumePromoCheckout(code, {
			user: () => userRef.current, active: activeVisit, login: openLogin,
			// Read INSIDE the start port, which `resumePromoCheckout` calls after its login await — the
			// same reason `userRef` is re-read there. A reader captured at click time would belong to the
			// signed-out visitor, and posting it after the sign-in throws.
			start: async (plan, period, terms) => {
				const documentSelection = readSelection?.();
				const outcome = await inPage.start(plan, period, {...terms, uiMode: "elements", immediatePerformanceConsent: acknowledged, ...(documentSelection ? {documentSelection} : {})});
				// Counted once the session exists, as the compact and first-month dialogs count theirs: a code
				// sold out while the dialog was open is refused at the mint, and no checkout opened.
				if (outcome === "ready") funnel.reportCheckoutOpened(price);
			},
		});
	};
	// Paid in place: the same confirming sentence — and the same "no claim about the new plan yet"
	// reasoning — as useCouponClaim's, because it is the same moment on the same sheet.
	const paid = () => {
		funnel.reportPaid(price);
		inPage.completed();
		showSuccessToast(banner.firstMonthOffer.dialog.checkout.confirming);
		onClose();
	};
	// The reader's own close (✕, Escape, the scrim) is a walk-away; the close after a payment is not, which
	// is why `paid` above closes through `onClose` directly.
	const dismiss = () => {
		funnel.reportDismissed();
		onClose();
	};
	return {dismiss, subscribe, acknowledged, setAcknowledged, busy: inPage.busy, expired: inPage.offerRefused, previewStatus: "ready",
		amountDueNow: null, clientSecret: inPage.clientSecret, checkoutError: inPage.error, paid,
		dismissSheet: inPage.dismiss};
}
